// Bezakli — variant that renders the user's standalone HTML template
// inside a full-bleed iframe. The HTML lives at /templates/bezakli.html
// and ships byte-for-byte exactly as authored except for a small inline
// customizer script we added inside the HTML — it reads the iframe URL's
// query params and replaces the hardcoded mock names / date / venue with
// the invitation's real values at runtime, plus retargets the countdown.
//
// We pass payload values via the URL because postMessage would race the
// iframe's first paint and the customizer needs to run before the
// IntersectionObserver / countdown scripts inside the HTML take effect.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/bezakli.html";

export function BezakliStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) params.set("date", t.date);
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.time) params.set("time", t.time);
  if (ctx.slug) params.set("slug", ctx.slug);
  if (t.musicTrackId) params.set("audio", `/music/${t.musicTrackId}.mp3`);
  // Bump when the static HTML changes — Vite doesn't hash files in
  // public/, so without a query string the browser caches the file.
  params.set("v", "3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Bezakli"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#F5EDDD",
      }}
    />
  );
}
